import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import type { Venue } from "@shared/schema";

// Map-anchored heatmap.
//
// Instead of redrawing the heat on every zoom/rotate (which reads as a flicker),
// we bake the heat blobs once into an off-screen canvas tied to a geographic
// LatLngBounds at a fixed reference zoom, then display it as an L.imageOverlay.
// Leaflet scales the overlay during zoom animations and the rotating overlayPane
// (leaflet-rotate) turns it with the tiles, so the heat is "glued" to the map and
// never redraws on a view change. We re-bake only when the venue/capacity data
// changes.
//
// HEAT_REFERENCE_ZOOM is the single knob for the locked-in look. The heat is
// baked at this zoom and scaled by Leaflet to whatever zoom is on screen. Set to
// 14 (a neighbourhood view) as a balance between readability when zoomed out and
// crispness when zoomed in.
const HEAT_REFERENCE_ZOOM = 14;
const HEAT_RADIUS = 30; // px at the reference zoom (matches the old getHeatRadius(14))
const HEAT_BLUR = 25;
const HEAT_MAX = 4.0;
const HEAT_MIN_OPACITY = 0.02;
// Cap on the rendered alpha channel so every heat colour is equally
// see-through. 64 (~25%) is the accumulated alpha at which a pixel is still
// fully light-blue (the 0.25 gradient stop), i.e. the light-blue blobs'
// effective peak opacity. Pixels below the cap keep their own alpha, which
// preserves the soft edge fade; hotter pixels still pick warmer colours from
// the LUT via their (uncapped) accumulated alpha.
const HEAT_ALPHA_CAP = 70;
const MAX_CANVAS_DIM = 4096; // cap so a large viewport never bakes a huge canvas

// Same capacity gradient as before (blue -> red).
const GRADIENT: Record<number, string> = {
  0.0: "#06b6d4",
  0.25: "#06b6d4",
  0.4: "#22d3a5",
  0.55: "#84cc16",
  0.7: "#facc15",
  0.85: "#f97316",
  1.0: "#ef4444",
};

// Soft circular brush, identical in spirit to simpleheat's: a black blob whose
// alpha falls off toward the edge, drawn off-canvas so only its blurred shadow
// lands on the brush canvas centred at (r2, r2).
function makeBrush(radius: number, blur: number): HTMLCanvasElement {
  const r2 = radius + blur;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = r2 * 2;
  const ctx = canvas.getContext("2d")!;
  ctx.shadowOffsetX = ctx.shadowOffsetY = r2 * 2;
  ctx.shadowBlur = blur;
  ctx.shadowColor = "black";
  ctx.beginPath();
  ctx.arc(-r2, -r2, radius, 0, Math.PI * 2, true);
  ctx.closePath();
  ctx.fill();
  return canvas;
}

// 256-entry RGBA lookup table built from the gradient stops; indexed by the
// accumulated alpha so denser areas read hotter. The gradient is constant, so
// the LUT is built once and reused across bakes.
let gradientLUT: Uint8ClampedArray | null = null;
function getGradientLUT(): Uint8ClampedArray {
  if (gradientLUT) return gradientLUT;
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 256;
  const ctx = canvas.getContext("2d")!;
  const g = ctx.createLinearGradient(0, 0, 0, 256);
  for (const stop in GRADIENT) g.addColorStop(+stop, GRADIENT[stop]);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 1, 256);
  gradientLUT = ctx.getImageData(0, 0, 1, 256).data;
  return gradientLUT;
}

type HeatPoint = [number, number, number]; // lat, lng, intensity (0..1)

interface BakeResult {
  url: string;
  bounds: L.LatLngBounds;
}

function bake(map: L.Map, points: HeatPoint[]): BakeResult | null {
  if (points.length === 0) return null;

  const z = HEAT_REFERENCE_ZOOM;
  const projected = points.map((p) => map.project(L.latLng(p[0], p[1]), z));

  const pad = HEAT_RADIUS + HEAT_BLUR;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const pt of projected) {
    if (pt.x < minX) minX = pt.x;
    if (pt.y < minY) minY = pt.y;
    if (pt.x > maxX) maxX = pt.x;
    if (pt.y > maxY) maxY = pt.y;
  }
  minX -= pad;
  minY -= pad;
  maxX += pad;
  maxY += pad;

  const width = Math.ceil(maxX - minX);
  const height = Math.ceil(maxY - minY);
  const longest = Math.max(width, height);
  const scale = longest > MAX_CANVAS_DIM ? MAX_CANVAS_DIM / longest : 1;

  const cw = Math.max(1, Math.round(width * scale));
  const ch = Math.max(1, Math.round(height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const radius = HEAT_RADIUS * scale;
  const blur = HEAT_BLUR * scale;
  const r2 = radius + blur;
  const brush = makeBrush(radius, blur);

  for (let i = 0; i < projected.length; i++) {
    const x = (projected[i].x - minX) * scale;
    const y = (projected[i].y - minY) * scale;
    const value = Math.min(points[i][2], HEAT_MAX);
    ctx.globalAlpha = Math.max(value / HEAT_MAX, HEAT_MIN_OPACITY);
    ctx.drawImage(brush, x - r2, y - r2);
  }

  const img = ctx.getImageData(0, 0, cw, ch);
  const lut = getGradientLUT();
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const alpha = d[i + 3];
    if (alpha) {
      const o = alpha * 4;
      d[i] = lut[o];
      d[i + 1] = lut[o + 1];
      d[i + 2] = lut[o + 2];
      if (alpha > HEAT_ALPHA_CAP) d[i + 3] = HEAT_ALPHA_CAP;
    }
  }
  ctx.putImageData(img, 0, 0);

  const nw = map.unproject(L.point(minX, minY), z);
  const se = map.unproject(L.point(maxX, maxY), z);
  return { url: canvas.toDataURL(), bounds: L.latLngBounds(nw, se) };
}

export function BakedHeatLayer({ venues }: { venues: Venue[] }) {
  const map = useMap();
  const overlayRef = useRef<L.ImageOverlay | null>(null);
  const signatureRef = useRef<string>("");

  useEffect(() => {
    const points: HeatPoint[] = venues.map((v) => [
      v.latitude as unknown as number,
      v.longitude as unknown as number,
      Math.max(((v as any).capacityPercent ?? 0) / 100, 0.15),
    ]);
    const signature = points
      .map((p) => p.join(","))
      .sort()
      .join("|");
    if (signature === signatureRef.current) return;
    signatureRef.current = signature;

    const baked = bake(map, points);
    if (!baked) {
      if (overlayRef.current) {
        map.removeLayer(overlayRef.current);
        overlayRef.current = null;
      }
      return;
    }

    if (overlayRef.current) {
      overlayRef.current.setBounds(baked.bounds);
      overlayRef.current.setUrl(baked.url);
    } else {
      overlayRef.current = L.imageOverlay(baked.url, baked.bounds, {
        interactive: false,
        opacity: 1,
        className: "buzzn-heat-overlay",
      });
      overlayRef.current.addTo(map);
    }
  }, [venues, map]);

  useEffect(() => {
    return () => {
      if (overlayRef.current) {
        map.removeLayer(overlayRef.current);
        overlayRef.current = null;
      }
    };
  }, [map]);

  return null;
}
